// OWNED BY the overview implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/overview/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// סקירה (overview) tab — two columns, split by the TYPE of information rather
// than chopped into equal thirds (the tab used to render seven equal-weight
// cards across three md={4} columns, so everything looked equally important and
// nothing did):
//
//   REFERENCE (things you read) → the narrow STICKY column: ✦ פרטי חיפוש
//     FIRST, then פרטים beneath it (customer feedback 2026-08: the search
//     profile is what a realtor needs at a glance, and the tall פרטים card
//     used to push it below the fold). Both are ordinary cards — the icon
//     rows, the completeness bar and the card surface are what make them
//     scannable, and a chrome-less "definition list" treatment here was tried
//     and reverted. For a PURE seller-side contact the column holds פרטים
//     alone — see the search-profile gate below.
//   WORKING SURFACE (things you act on) → the wide column, cards.
//     1. the task strip — one tinted row, not a card (a card around two lines
//        of text was the exact "card tax" this redesign removes)
//     2. סיכום AI — the ONLY colour-filled card on the screen, which is what
//        makes it read as primary
//     3. פעילות אחרונה — composer + rows + link to the full timeline
//     4. המיניסייט — interactive (WhatsApp share, per-listing views/likes), so
//        it belongs here and NOT summarised into the reference column
//
// Desktop (>= md) is TWO columns, not three; under RTL the first DOM child
// renders rightmost, so the reference column is first here. Below md it is ONE
// column: the task strip first (a single thin actionable line), then the
// REFERENCE panels (✦ פרטי חיפוש, then פרטים), then the working cards. The
// original mobile order buried both reference panels at the very bottom
// ("action-first") and customers reported exactly that (2026-08): they open a
// contact and cannot see the search profile and the main details at a glance —
// so reference now leads. Below sm the panels' TabCard shells additionally
// drop their chrome and render as divider-separated full-bleed sections, per
// the page-wide convention.
//
// The standalone הערות panel was removed from this tab — notes still live in
// the dedicated הערות tab, and the quick composer + notes-merged feed inside
// פעילות אחרונה keeps "jot a note without leaving סקירה" working.
// Everything renders instantly from sessionCache (warmed by preloadOverviewTab
// on contacts-table row hover) with subtle inline skeletons on true cold start.
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack } from "@material-ui/core";
import { contactShowsBuyerSurfaces } from "../../../../../utils/contacts/contactSides";
import { useContactSurfaceDown } from "../contactSurface";
import { useSessionCacheValue } from "../data/sessionCache";
import { useOverviewNotes } from "./overview/useOverviewNotes";
import { usePropertyNotes } from "./notes/usePropertyNotes";
import { OverviewDetailsPanel } from "./overview/OverviewDetailsPanel";
import { OverviewSearchProfilePanel } from "./overview/OverviewSearchProfilePanel";
import { OverviewAiSummaryCard } from "./overview/OverviewAiSummaryCard";
import { OverviewActivityPanel } from "./overview/OverviewActivityPanel";
import { OverviewMinisitePanel } from "./overview/OverviewMinisitePanel";
import { OverviewTaskStrip } from "./overview/OverviewTaskStrip";
import {
  preloadOverview,
  deriveLastTouch,
  contactDetailsCacheKey,
  fetchContactDetailsResponse,
  sortNotesNewestFirst,
  CONTACT_DETAILS_MAX_AGE_MS
} from "./overview/overviewData";
import type { RealEstateContact, REST_schema } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

// The reference column is a FIXED narrow column, not a fraction of the screen.
// Sized for CARDS: OverviewPanel/TabCard spends 20px of padding per side and
// פרטים's icon chip + gap another 48px before the value starts, so a bare
// ~264px (which suited the chrome-less experiment) left values in a ~176px
// gutter. 320/352 puts the value column back where the old three-column grid
// had it (~360px per column at lg) without narrowing the working column below
// ~470px at md. No xs/sm entry on purpose — below md everything stacks.
const OVERVIEW_REFERENCE_COL_WIDTH = { md: 320, lg: 352 } as const;

// Breathing room between the rail and whatever is pinned above it. The pinned
// height itself comes from the page-level CSS variable
// --contact-detail-sticky-offset (published by ContactDetailView's sticky
// header+strip block, AppBar included); the 64px fallback covers hosts that
// don't publish it (legacy layout) — the bare fixed AppBar.
const OVERVIEW_REFERENCE_STICKY_TOP = 16;
const OVERVIEW_STICKY_OFFSET_FALLBACK_PX = 64;
const OVERVIEW_STICKY_TOP_CSS = `calc(var(--contact-detail-sticky-offset, ${OVERVIEW_STICKY_OFFSET_FALLBACK_PX}px) + ${OVERVIEW_REFERENCE_STICKY_TOP}px)`;

export default function OverviewTab({ contact }: ContactTabComponentProps): JSX.Element {
  // Surface-relative, like TabCard — see `contactSurface.tsx`. Same breakpoint TabCard
  // flips on, so the two must read the same source or the card chrome and the layout
  // inside it disagree about which mode they are in.
  const isMobile = useContactSurfaceDown("sm");
  // The reference column needs ~320px of its own before the working column
  // starts feeling squeezed, so the two-column layout only starts at md.
  // Between sm and md everything stacks into one full-width column — which is
  // exactly what the previous `md={4}` grid did there too (it collapsed to
  // xs={12}).
  const isNarrow = useContactSurfaceDown("md");

  // Shared with the timeline tab: same key, same fetcher shape (full
  // get-details response) — whichever tab fetches first serves both.
  // details === undefined means "not in cache yet" (cold start → skeletons).
  const { data: details, error: detailsError } =
    useSessionCacheValue<REST_schema.ContactDetailsResponseSchema>(
      contactDetailsCacheKey(contact.id),
      () => fetchContactDetailsResponse(contact.id),
      { maxAgeMs: CONTACT_DETAILS_MAX_AGE_MS }
    );
  // Failed with nothing cached → error states, not eternal skeletons.
  const detailsFailed = details === undefined && detailsError !== null;

  // Realtime notes (own + org-shared), mirrored into the shared notes cache.
  // Still needed after the הערות panel was dropped: `notes` feeds the last-touch
  // calculation and the activity feed, `addNote` backs the composer inside
  // פעילות אחרונה, and `notesLoading` gates the last-touch skeleton.
  const { notes, isLoading: notesLoading, addNote } = useOverviewNotes(contact.id);

  // The notes she wrote on this contact's PROPERTY join the activity feed, labelled — the
  // first thing the board's side panel shows is this tab (see `usePropertyNotes`). Not the
  // last-touch figure: that stays the contact's own, as every other screen computes it.
  const propertyNotes = usePropertyNotes(contact);
  const feedNotes = useMemo(
    () => (propertyNotes.length ? sortNotesNewestFirst([...notes, ...propertyNotes]) : notes),
    [notes, propertyNotes]
  );

  const lastTouch = useMemo(
    () => deriveLastTouch(contact, details, notes),
    [contact, details, notes]
  );

  // STICKY DEGRADATION. `position: sticky` with only a `top` offset pins a
  // too-tall column's TOP against the viewport, which makes its bottom
  // permanently unreachable — the reader can never scroll to פרטי חיפוש. So we
  // stick ONLY while the column fits under the AppBar; when it doesn't (many
  // detail rows, a short window, browser zoom) it falls back to scrolling with
  // the page, exactly as it did before sticky went live. Measured with a
  // ResizeObserver because the column's height changes with the DATA (tags,
  // legacy notes, extra phones), not just with the viewport.
  const referenceColumnRef = useRef<HTMLDivElement | null>(null);
  const [canStick, setCanStick] = useState(true);
  useEffect(() => {
    const element = referenceColumnRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const check = (): void => {
      // Height budget = the pinned block above (CSS var, AppBar-inclusive) +
      // the rail's own offset + breathing room at the bottom.
      const pinnedAbove =
        parseFloat(
          getComputedStyle(element).getPropertyValue("--contact-detail-sticky-offset")
        ) || OVERVIEW_STICKY_OFFSET_FALLBACK_PX;
      setCanStick(
        element.scrollHeight + pinnedAbove + OVERVIEW_REFERENCE_STICKY_TOP + 24 <=
          window.innerHeight
      );
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(element);
    window.addEventListener("resize", check);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [isNarrow]);

  // Reference — the SAME cards at every breakpoint (cards >= sm, flat
  // divider-separated sections < sm via TabCard). Only their column changes.
  const detailsPanel = (
    <OverviewDetailsPanel
      contact={contact}
      lastTouch={lastTouch}
      lastTouchLoading={(details === undefined && !detailsFailed) || notesLoading}
    />
  );
  /**
   * ✦ פרטי חיפוש IS ABSENT FOR A PURE SELLER — because of WHO THE CONTACT IS, never
   * because the fields are empty (customer reject 2026-08-25: *"for a SELLER/LANDLORD
   * that is NOT a buyer/renter we don't need to show the פרטי חיפוש — even if
   * defined"*). A מוכר carrying a budget and a city from an old import would otherwise
   * be shown somebody else's import as their own requirements. The rule, and why a
   * mixed-type or untyped contact keeps the panel, is in `utils/contacts/contactSides`
   * — the same predicate that hides the התאמות tab, so a seller loses both buyer-side
   * surfaces together instead of one of them.
   *
   * GATED HERE rather than by an early `return null` inside the panel, for two reasons.
   * A component that returns null still MOUNTS, and this one calls `usePropertyTypes()`
   * unconditionally at its top (see its own note) — so hiding from the inside would
   * still fire the property-type facet request for a panel nobody will see. And the
   * reference COLUMN's composition is this file's business: below it decides whether
   * that column carries two panels or one.
   *
   * Both layouts survive the absence without a dangling gap: the desktop column is a
   * `gap` Stack, which emits nothing for a slot that renders no DOM, and on mobile
   * TabCard draws each section's divider with `&:last-child { borderBottom: 0 }`, so
   * the last section is still the one without a rule under it.
   */
  const searchProfilePanel = contactShowsBuyerSurfaces(contact) ? (
    <OverviewSearchProfilePanel contact={contact} />
  ) : null;

  // Working surface — same components at every breakpoint; only their column
  // changes. TabCard already handles its own desktop/mobile chrome flip.
  const taskStrip = <OverviewTaskStrip contact={contact} />;
  const aiSummaryCard = <OverviewAiSummaryCard contact={contact} />;
  const activityPanel = (
    <OverviewActivityPanel
      details={details}
      detailsFailed={detailsFailed}
      notes={feedNotes}
      addNote={addNote}
    />
  );
  const minisitePanel = <OverviewMinisitePanel contact={contact} />;

  if (isNarrow) {
    // ONE column, reference-first (customer feedback 2026-08): the realtor
    // opens a contact to SEE who this is and what they're looking for — the
    // search profile and the main details lead, right after the one-line task
    // strip; the working cards follow.
    // < sm: TabCard drops its chrome and draws the between-section dividers
    // itself, so the stack takes no gap (one continuous scroll). >= sm the
    // panels are real cards and need one. The task strip is neither, so it
    // carries its own bottom margin in the gap-less case.
    return (
      <Stack sx={{ gap: isMobile ? 0 : 2 }}>
        <Box sx={{ mb: isMobile ? 1 : 0 }}>{taskStrip}</Box>
        {searchProfilePanel}
        {detailsPanel}
        {aiSummaryCard}
        {activityPanel}
        {minisitePanel}
      </Stack>
    );
  }

  return (
    // Plain flex row rather than a Grid: the reference column is a FIXED narrow
    // column, not a fraction of the screen. Default `align-items: stretch` is
    // load-bearing — that column has to be as tall as the working column for
    // its sticky child to have any travel.
    <Box sx={{ display: "flex", gap: 2.5 }}>
      {/* Reference column — first in the DOM ⇒ rightmost under RTL. */}
      <Box sx={{ width: OVERVIEW_REFERENCE_COL_WIDTH, flexShrink: 0 }}>
        <Stack
          ref={referenceColumnRef}
          sx={{
            gap: 2,
            ...(canStick
              ? { position: "sticky", top: OVERVIEW_STICKY_TOP_CSS }
              : // Too tall to pin — scroll with the page. NOT an inner
                // scrollport: `overflow` here would clip the cards' shadow.
                { position: "static" })
          }}
        >
          {searchProfilePanel}
          {detailsPanel}
        </Stack>
      </Box>

      {/* minWidth:0 — without it a long unbroken value inside a card sets the
          flex item's min-content width and pushes the reference column
          off-screen. */}
      <Stack sx={{ flexGrow: 1, minWidth: 0, gap: 2 }}>
        {taskStrip}
        {aiSummaryCard}
        {activityPanel}
        {minisitePanel}
      </Stack>
    </Box>
  );
}

// Badge for the tab strip (hook — may use sessionCache). Overview shows no
// badge by design (it's the landing tab).
export function useOverviewTabBadge(_contact: RealEstateContact): TabBadge {
  return null;
}

// Cache warm-up fired on contacts-table row hover and page mount. Warms the
// shared contact-details + notes keys, the minisite favorites doc, and the
// latest transcribed call — all deduped/stale-while-revalidate via fetchCached.
export function preloadOverviewTab(contact: RealEstateContact): void {
  try {
    preloadOverview(contact);
  } catch {
    // preloads are best-effort by contract
  }
}
